import * as React from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'
import { Slot } from '@radix-ui/react-slot'
import { Controller, ControllerProps, FieldPath, FieldValues, FormProvider } from 'react-hook-form'

import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import { FormFieldContext, FormItemContext } from './FormContext'
import { useFormField } from './useFormField'

const Form: typeof FormProvider = FormProvider

const FormField = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  const ctx = React.useMemo(() => ({ name: props.name }), [props.name])
  return (
    <FormFieldContext value={ctx}>
      <Controller {...props} />
    </FormFieldContext>
  )
}

const FormItem = ({ ref, className, ...props }: React.ComponentPropsWithRef<'div'>) => {
  const id = React.useId()
  const ctx = React.useMemo(() => ({ id }), [id])
  return (
    <FormItemContext value={ctx}>
      <div ref={ref} className={cn(`ui-space-y-2`, className)} {...props} />
    </FormItemContext>
  )
}
FormItem.displayName = `FormItem`

const FormLabel = ({ ref, className, ...props }: React.ComponentPropsWithRef<typeof LabelPrimitive.Root>) => {
  const { error, formItemId } = useFormField()

  return <Label ref={ref} className={cn(error && `text-destructive`, className)} htmlFor={formItemId} {...props} />
}
FormLabel.displayName = `FormLabel`

const FormControl = ({ ref, ...props }: React.ComponentPropsWithRef<typeof Slot>) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField()

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      {...props}
    />
  )
}
FormControl.displayName = `FormControl`

const FormDescription = ({ ref, className, ...props }: React.ComponentPropsWithRef<'p'>) => {
  const { formDescriptionId } = useFormField()

  return <p ref={ref} id={formDescriptionId} className={cn(`ui-text-sm ui-text-muted-foreground`, className)} {...props} />
}
FormDescription.displayName = `FormDescription`

const FormMessage = ({ ref, className, children, ...props }: React.ComponentPropsWithRef<'p'>) => {
  const { error, formMessageId } = useFormField()
  const body = error ? String(error?.message) : children

  if (!body) {
    return null
  }

  return (
    <p ref={ref} id={formMessageId} className={cn(`ui-text-sm ui-font-medium ui-text-destructive`, className)} {...props}>
      {body}
    </p>
  )
}
FormMessage.displayName = `FormMessage`

export { Form, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField }
